import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { getLocaleForCurrency } from '@/lib/currencyConfig';
import { formatCurrency } from '@/lib/currency';
import { Loader2, CreditCard, Smartphone } from 'lucide-react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { auth } from '@/lib/firebase';

/**
 * A2B / Cardstream Hosted Payment Page.
 *
 * User continues to A2B’s secure page (card, Apple Pay, Google Pay when enabled
 * in MMS). No card data touches YSR servers. After payment, gateway redirects
 * back and PaymentReturnHandler shows the result.
 */
const A2BPaymentForm = ({
  amount,
  artistId,
  currency: propCurrency = 'GBP',
  description,
  onSuccess,
  onCancel,
  mode = 'artist',
  requestData = {},
  returnPath: returnPathProp,
}) => {
  const { toast } = useToast();
  const mobile = useMobile();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  let currencyContext = null;
  let currency = propCurrency || 'GBP';
  try {
    currencyContext = useCurrency();
    currency = propCurrency || currencyContext?.currency || 'GBP';
  } catch {
    currency = propCurrency || 'GBP';
  }

  const finalCurrency = currency || 'GBP';

  const format = propCurrency
    ? amt => {
        const locale = getLocaleForCurrency(propCurrency);
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: propCurrency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amt);
      }
    : currencyContext?.format || (amt => formatCurrency(amt ?? 0, finalCurrency));

  const handleSubmit = async e => {
    e.preventDefault();

    if (!artistId) {
      setError('Artist ID is required for payment processing.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const functionsInstance = getFunctions(auth.app, 'us-central1');
      const createSession = httpsCallable(functionsInstance, 'createA2BPaymentSession');

      const amountNumber = Number(amount);
      if (isNaN(amountNumber) || amountNumber <= 0) {
        throw new Error('Invalid amount for payment.');
      }

      const returnPath =
        returnPathProp ||
        (typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search || ''}`
          : '/');

      const sessionResult = await createSession({
        amount: amountNumber,
        currency: finalCurrency,
        artistId,
        mode,
        returnPath,
        requestData: { ...requestData },
        orderReference: requestData.orderReference,
      });

      const data = sessionResult.data;

      if (!data || !data.success || !data.hostedUrl || !data.fields) {
        throw new Error(data?.message || 'Failed to create payment session.');
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.hostedUrl;
      form.style.display = 'none';

      Object.entries(data.fields).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error('💳 A2B payment error:', err);

      let message = err.message || 'Payment failed. Please try again.';
      if (err.details) {
        message =
          typeof err.details === 'string'
            ? err.details
            : err.details.message || message;
      }

      setError(message);
      toast({
        title: 'Payment Failed',
        description: message,
        variant: 'destructive',
      });
      setProcessing(false);
    }
  };

  if (!artistId) {
    return (
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-6">
          <div className="text-center text-gray-400">
            <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm">Artist ID is required for payment processing.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`${
        mobile.isMobile
          ? 'bg-gray-900/95 border-0 shadow-lg'
          : 'bg-gray-800/50'
      } border-gray-700`}
    >
      <CardHeader className={mobile.isMobile ? 'pb-3 px-4 pt-4' : 'pb-4'}>
        <CardTitle
          className={`${mobile.isMobile ? 'text-lg' : 'text-xl'} text-white flex items-center gap-2`}
        >
          <CreditCard className="w-5 h-5 text-purple-400 shrink-0" />
          Pay securely
        </CardTitle>
        <CardDescription className={mobile.isMobile ? 'text-xs' : 'text-sm'}>
          <span className="flex items-start gap-2 text-gray-300">
            <Smartphone className="w-4 h-4 mt-0.5 text-purple-400 shrink-0" />
            <span>
              You will now be redirected to our secure payments partner page,{' '}
              <strong className="text-gray-200 font-medium">A2B Payments</strong>. After you submit
              payment, you will be returned here. Thank you.
            </span>
          </span>
          {description ? (
            <span className="block mt-2 text-gray-400">{description}</span>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className={mobile.isMobile ? 'px-4 pb-4 pt-0' : 'px-6 pb-6'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="text-red-400 text-sm p-3 bg-red-900/20 border border-red-800 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex flex-col gap-3 pt-1">
            <Button
              type="submit"
              disabled={processing}
              className={`w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 ${
                mobile.isMobile ? 'h-12 text-base font-semibold' : 'h-11'
              }`}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Opening secure payment…
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Continue to pay {format(amount)}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={processing}
              className={`w-full ${mobile.isMobile ? 'h-12 text-base' : 'h-11'}`}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default A2BPaymentForm;
